import { useTranslation } from 'react-i18next';
import type { SubscriptionResponse, UsageResponse } from '@/api';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Section, SectionBody } from '@/components/Ui/Section';
import { formatEur, formatNumber, vfiatToEur } from '@/lib/money';
import { shortDate } from '@/lib/relative-time';
import shared from '../AccountScreens.module.css';
import { USAGE_ORDER, USAGE_SERVICES } from './billing-labels';

interface Row {
  service: string;
  used: number;
  limit: number;
}

export function UsageSection({
  usage,
  subscription,
}: {
  usage: UsageResponse[] | null;
  subscription: SubscriptionResponse | null;
}) {
  const { t, i18n } = useTranslation();

  const rows: Row[] = USAGE_ORDER.map((service) => {
    const u = usage?.find((x) => x.service === service);
    return { service, used: u?.used ?? 0, limit: u?.limit ?? 0 };
  });

  const amount = (row: Row, n: number) => {
    const svc = USAGE_SERVICES[row.service];
    return svc.unit === 'kb'
      ? t('billing.usage.mb', { mb: formatNumber(Math.round((n / 1024) * 10) / 10, i18n.language) })
      : formatNumber(n, i18n.language);
  };

  const excess = (row: Row) => (row.limit < 0 ? 0 : Math.max(0, row.used - row.limit));

  const period =
    subscription?.current_period_start && subscription.current_period_end
      ? t('billing.usage.period', {
          from: shortDate(subscription.current_period_start, i18n.language),
          to: shortDate(subscription.current_period_end, i18n.language),
        })
      : undefined;

  return (
    <Section id="usage" title={t('billing.usage.title')} subtitle={period}>
      {usage === null ? (
        <SectionBody>
          <EmptyText>{t('billing.usage.none')}</EmptyText>
        </SectionBody>
      ) : (
        <DataTable
          caption={t('billing.usage.title')}
          rows={rows}
          rowKey={(r) => r.service}
          columns={[
            {
              key: 'what',
              header: t('billing.usage.colWhat'),
              render: (r) => {
                const svc = USAGE_SERVICES[r.service];
                return (
                  <span>
                    {t(svc.labelKey)}
                    {svc.noteKey && <span className={shared.help}> {t(svc.noteKey)}</span>}
                  </span>
                );
              },
            },
            {
              key: 'count',
              header: t('billing.usage.colCount'),
              align: 'num',
              render: (r) => amount(r, r.used),
            },
            {
              key: 'included',
              header: t('billing.usage.colIncluded'),
              align: 'num',
              render: (r) => (r.limit < 0 ? t('billing.usage.unlimited') : amount(r, r.limit)),
            },
            {
              key: 'price',
              header: t('billing.usage.colPrice'),
              align: 'num',
              render: (r) =>
                USAGE_SERVICES[r.service].unit === 'kb'
                  ? t('billing.usage.pricePerMb')
                  : formatEur(vfiatToEur(USAGE_SERVICES[r.service].rateVfiat), i18n.language, 3, 4),
            },
            {
              key: 'from',
              header: t('billing.usage.colPaidFrom'),
              render: (r) =>
                r.used === 0
                  ? '–'
                  : excess(r) > 0
                    ? r.limit > 0
                      ? t('billing.usage.allowanceThenCredit')
                      : t('billing.usage.credit')
                    : t('billing.usage.allowance'),
            },
            {
              key: 'credit',
              header: t('billing.usage.colFromCredit'),
              align: 'num',
              render: (r) =>
                formatEur(
                  vfiatToEur(excess(r) * USAGE_SERVICES[r.service].rateVfiat),
                  i18n.language,
                  2,
                  4
                ),
            },
          ]}
        />
      )}
      <div className={shared.footer}>
        <span className={shared.help}>{t('billing.usage.footnote')}</span>
      </div>
    </Section>
  );
}
